// src/components/Presentation/AudiencePanel.js
import React, { useState } from 'react';
import { 
  Users, 
  MessageCircle, 
  BarChart3, 
  X, 
  Filter, 
  Search,
  Clock,
  ThumbsUp,
  Eye
} from 'lucide-react';

const AudiencePanel = ({ session, participants, interactions, onClose }) => {
  const [activeTab, setActiveTab] = useState('participants');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');

  const tabs = [
    { id: 'participants', label: 'Participants', icon: Users, count: participants.length },
    { id: 'questions', label: 'Questions', icon: MessageCircle, count: interactions.filter(i => i.type === 'question_submit').length },
    { id: 'polls', label: 'Polls', icon: BarChart3, count: interactions.filter(i => i.type === 'poll_vote').length },
    { id: 'analytics', label: 'Analytics', icon: Eye, count: null }
  ];

  const filteredParticipants = participants.filter(participant =>
    participant.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const questions = interactions
    .filter(i => i.type === 'question_submit')
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  const pollResults = interactions
    .filter(i => i.type === 'poll_vote')
    .reduce((acc, interaction) => {
      const slideId = interaction.slideId;
      if (!acc[slideId]) {
        acc[slideId] = {};
      }
      interaction.data.selectedOptions.forEach(option => {
        acc[slideId][option] = (acc[slideId][option] || 0) + 1;
      });
      return acc;
    }, {});

  return (
    <div className="w-96 bg-white border-l border-gray-200 flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Audience</h3>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Session Info */}
        {session && (
          <div className="mt-3 text-sm text-gray-600">
            <div className="flex items-center justify-between">
              <span>Session: {session.sessionCode}</span>
              <span className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>Live</span>
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex">
          {tabs.map((tab) => {
            const IconComponent = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex-1 flex items-center justify-center space-x-2 py-3 text-sm font-medium transition-colors
                  ${activeTab === tab.id
                    ? 'border-b-2 border-indigo-500 text-indigo-600'
                    : 'text-gray-600 hover:text-gray-900'
                  }
                `}
              >
                <IconComponent className="w-4 h-4" />
                <span>{tab.label}</span>
                {tab.count !== null && (
                  <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs">
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Search and Filters */}
      {(activeTab === 'participants' || activeTab === 'questions') && (
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={`Search ${activeTab}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'participants' && (
          <ParticipantsList participants={filteredParticipants} />
        )}
        
        {activeTab === 'questions' && (
          <QuestionsList questions={questions.filter(q => 
            searchTerm === '' || q.data.text.toLowerCase().includes(searchTerm.toLowerCase())
          )} />
        )}
        
        {activeTab === 'polls' && (
          <PollResultsList pollResults={pollResults} />
        )}
        
        {activeTab === 'analytics' && (
          <AnalyticsPanel session={session} interactions={interactions} />
        )}
      </div>
    </div>
  );
};

// Participants List Component
const ParticipantsList = ({ participants }) => {
  return (
    <div className="p-4">
      {participants.length > 0 ? (
        <div className="space-y-2">
          {participants.map((participant) => (
            <div key={participant.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
              <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                <span className="text-sm font-medium text-indigo-600">
                  {participant.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">{participant.name}</p>
                <p className="text-xs text-gray-500">
                  Joined {new Date(participant.joinedAt).toLocaleTimeString()}
                </p>
              </div>
              {participant.isHost && (
                <span className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded-full text-xs font-medium">
                  Host
                </span>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center text-gray-500 py-8">
          <Users className="w-8 h-8 mx-auto mb-2 text-gray-400" />
          <p>No participants yet</p>
        </div>
      )}
    </div>
  );
};

// Questions List Component
const QuestionsList = ({ questions }) => {
  return (
    <div className="p-4">
      {questions.length > 0 ? (
        <div className="space-y-3">
          {questions.map((question) => (
            <div key={question.id} className="bg-gray-50 rounded-lg p-4">
              <p className="text-gray-900 mb-2">{question.data.text}</p>
              <div className="flex items-center justify-between text-sm text-gray-500">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-1">
                    <Clock className="w-3 h-3" />
                    <span>{new Date(question.timestamp).toLocaleTimeString()}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <ThumbsUp className="w-3 h-3" />
                    <span>{question.data.upvotes || 0}</span>
                  </div>
                </div>
                <button className="px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-xs font-medium hover:bg-indigo-200 transition-colors">
                  Mark Answered
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center text-gray-500 py-8">
          <MessageCircle className="w-8 h-8 mx-auto mb-2 text-gray-400" />
          <p>No questions yet</p>
        </div>
      )}
    </div>
  );
};

// Poll Results List Component
const PollResultsList = ({ pollResults }) => {
  return (
    <div className="p-4">
      {Object.keys(pollResults).length > 0 ? (
        <div className="space-y-4">
          {Object.entries(pollResults).map(([slideId, results]) => (
            <div key={slideId} className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-3">Poll Results - Slide {slideId}</h4>
              <div className="space-y-2">
                {Object.entries(results).map(([option, count]) => {
                  const total = Object.values(results).reduce((sum, c) => sum + c, 0);
                  const percentage = total > 0 ? (count / total) * 100 : 0;
                  
                  return (
                    <div key={option} className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">Option {parseInt(option) + 1}</span>
                      <div className="flex items-center space-x-2">
                        <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-indigo-500 transition-all duration-500"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-gray-900 w-12 text-right">
                          {count} ({percentage.toFixed(0)}%)
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center text-gray-500 py-8">
          <BarChart3 className="w-8 h-8 mx-auto mb-2 text-gray-400" />
          <p>No poll responses yet</p>
        </div>
      )}
    </div>
  );
};

// Analytics Panel Component
const AnalyticsPanel = ({ session, interactions }) => {
  const getEngagementMetrics = () => {
    const totalInteractions = interactions.length;
    const uniqueParticipants = new Set(interactions.map(i => i.participantId)).size;
    const avgInteractionsPerParticipant = uniqueParticipants > 0 ? totalInteractions / uniqueParticipants : 0;
    
    return {
      totalInteractions,
      uniqueParticipants,
      avgInteractionsPerParticipant
    };
  };

  const metrics = getEngagementMetrics();

  return (
    <div className="p-4 space-y-6">
      {/* Overview Stats */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-gray-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-indigo-600">{session?.participants?.length || 0}</div>
          <div className="text-sm text-gray-600">Total Participants</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-green-600">{metrics.totalInteractions}</div>
          <div className="text-sm text-gray-600">Total Interactions</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-purple-600">{metrics.uniqueParticipants}</div>
          <div className="text-sm text-gray-600">Active Participants</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-orange-600">{metrics.avgInteractionsPerParticipant.toFixed(1)}</div>
          <div className="text-sm text-gray-600">Avg. Engagement</div>
        </div>
      </div>

      {/* Interaction Types Breakdown */}
      <div>
        <h4 className="font-medium text-gray-900 mb-3">Interaction Types</h4>
        <div className="space-y-2">
          {[
            { type: 'poll_vote', label: 'Poll Votes', color: 'bg-blue-500' },
            { type: 'question_submit', label: 'Questions', color: 'bg-green-500' },
            { type: 'question_upvote', label: 'Question Upvotes', color: 'bg-yellow-500' }
          ].map(({ type, label, color }) => {
            const count = interactions.filter(i => i.type === type).length;
            const percentage = metrics.totalInteractions > 0 ? (count / metrics.totalInteractions) * 100 : 0;
            
            return (
              <div key={type} className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${color}`}></div>
                  <span className="text-sm text-gray-700">{label}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${color} transition-all duration-500`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-gray-900 w-8 text-right">
                    {count}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Session Duration */}
      {session?.startedAt && (
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Session Duration</h4>
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-lg font-semibold text-gray-900">
              {Math.floor((Date.now() - session.startedAt.toDate()) / 1000 / 60)} minutes
            </div>
            <div className="text-sm text-gray-600">
              Started at {session.startedAt.toDate().toLocaleTimeString()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AudiencePanel;